import React, { useCallback, useEffect, useState } from 'react';
import SafeHtml from '../../components/SafeHtml';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { dirAttr, resolveLocalized } from '../../i18n/contentLocales';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { listRunChallenges, listRunStations, listSubmissions } from '../../api/runs';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { MultilingualString, RunChallenge, RunStation, Submission } from '../../api/types';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';
import StationTipsDialog from '../../components/StationTipsDialog';
import BackLink from '../../components/BackLink';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import StationAddress from '../../components/StationAddress';

const StationChallengeList: React.FC = () => {
  const { runId, stationId } = useParams<{ runId: string; stationId: string }>();
  const { user } = useAuth();
  const { t, i18n } = useTranslation('runs');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Restore whichever view (map vs. list) the participant came from (#4 —
  // otherwise "back" always dropped them onto List, even from Map).
  const stationsBackTo =
    searchParams.get('from') === 'map'
      ? `/runs/${runId}/participant/stations?view=map`
      : `/runs/${runId}/participant/stations`;

  const [challenges, setChallenges] = useState<RunChallenge[]>([]);
  const [station, setStation] = useState<RunStation | null>(null);
  const [stationNumber, setStationNumber] = useState<number | null>(null);
  // Keyed by run_challenge_id. A bare ID set used to live here, which threw
  // away is_scored and the points — so a graded answer was indistinguishable
  // from a merely submitted one, no matter how fresh the data was.
  const [submissionsByChallenge, setSubmissionsByChallenge] = useState<Map<string, Submission>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tipsOpen, setTipsOpen] = useState(false);

  const orgSlug = user?.organization_slug;

  const load = useCallback(async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!orgSlug || !runId || !stationId) return;
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [challengesResp, stationsResp, submissionsResp] = await Promise.all([
        listRunChallenges(orgSlug, runId, stationId),
        listRunStations(orgSlug, runId),
        listSubmissions(orgSlug, runId),
      ]);
      const sorted = [...challengesResp.items].sort((a, b) => a.sort_order - b.sort_order);
      setChallenges(sorted);
      const orderedStations = [...stationsResp.items].sort((a, b) => a.sort_order - b.sort_order);
      const idx = orderedStations.findIndex((s) => s.id === stationId);
      setStation(idx >= 0 ? orderedStations[idx] : null);
      setStationNumber(idx >= 0 ? idx + 1 : null);
      setSubmissionsByChallenge(
        new Map(submissionsResp.items.map((s: Submission) => [s.run_challenge_id, s])),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, runId, stationId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A teammate's answer and a coordinator's grade both land here without this
  // screen being touched — re-read them instead of showing the state as of
  // whenever the station was opened.
  useAutoRefresh(() => load({ quiet: true }));

  // Full UI language (not collapsed to de/en): org content locales are free
  // (e.g. ar, fa) — an Arabic-browser participant gets Arabic content, RTL.
  const activeLang = i18n.language;
  const titleOf = (ml: MultilingualString) => resolveLocalized(ml, activeLang).text;
  const dirOf = (ml: MultilingualString) => dirAttr(resolveLocalized(ml, activeLang).locale);

  if (loading) return <LoadingSpinner />;

  return (
    <Stack spacing={2}>
      <BackLink
        to={stationsBackTo}
        label={t('participant.stationChallenges.backToStations')}
      />
      {station?.title_image_url && (
        <Box
          component="img"
          src={station.title_image_url}
          alt={titleOf(station.title) || t('participant.stationChallenges.title')}
          sx={{
            width: '100%',
            maxHeight: 220,
            objectFit: 'cover',
            borderRadius: 2,
            display: 'block',
          }}
        />
      )}
      <Typography variant="h5" component="h1" dir={station ? dirOf(station.title) : undefined}>
        {station
          ? `${stationNumber != null ? `${stationNumber}. ` : ''}${
              titleOf(station.title) || t('participant.stationChallenges.title')
            }`
          : t('participant.stationChallenges.title')}
      </Typography>

      {station && titleOf(station.description) && (
        <SafeHtml html={titleOf(station.description)} dir={dirOf(station.description)} />
      )}

      {station?.address && (
        <StationAddress
          address={station.address}
          lat={station.location_lat}
          lng={station.location_lng}
          label={titleOf(station.title)}
        />
      )}

      {station?.tips && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<LightbulbOutlinedIcon />}
          onClick={() => setTipsOpen(true)}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('participant.stationChallenges.tipsButton')}
        </Button>
      )}

      {station?.tips && (
        <StationTipsDialog tips={station.tips} open={tipsOpen} onClose={() => setTipsOpen(false)} />
      )}

      {error && <ErrorAlert message={error} onRetry={load} />}

      {challenges.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
          {t('participant.stationChallenges.empty')}
        </Typography>
      )}

      {challenges.map((challenge, idx) => {
        const submission = submissionsByChallenge.get(challenge.id);
        const done = submission !== undefined;
        const scored = submission?.is_scored === true;
        return (
          <Card key={challenge.id} variant={done ? 'outlined' : 'elevation'}>
            <CardActionArea
              onClick={() =>
                navigate(
                  `/runs/${runId}/participant/stations/${stationId}/challenges/${challenge.id}`,
                )
              }
            >
              <CardContent>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                  {/* Same green throughout — the trophy marks a graded answer,
                      the tick a delivered one, so the state reads at a glance
                      without a second colour to learn. */}
                  {scored ? (
                    <EmojiEventsIcon color="success" />
                  ) : done ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon color="disabled" />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" noWrap dir={dirOf(challenge.title)}>
                      {idx + 1}. {titleOf(challenge.title) || t('participant.challenge.untitled', { defaultValue: `Aufgabe ${idx + 1}` })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {t(`participant.challenge.types.${challenge.challenge_type}`, {
                        defaultValue: challenge.challenge_type,
                      })}
                    </Typography>
                  </Box>
                  <Chip
                    label={
                      scored
                        ? t('participant.challenge.scoredWithPoints', {
                            points: submission?.points ?? 0,
                            maxPoints:
                              submission?.max_points ?? challenge.scoring_rules.max_points ?? 0,
                          })
                        : done
                          ? t('participant.challenge.submitted')
                          : t('participant.challenge.points', {
                              points: challenge.scoring_rules.max_points ?? 0,
                            })
                    }
                    size="small"
                    color={done ? 'success' : 'default'}
                    variant={done ? 'filled' : 'outlined'}
                  />
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Stack>
  );
};

export default StationChallengeList;
